<template>
  <div class="d-flex align-center">
    <helper-image-input
      :value="value"
      :max-size-in-mb="maxSizeInMb"
      field-name="logo"
      :accept-type="acceptTypes.join(',')"
      @input="onFileSelected"
    >
      <template v-slot:activator>
        <div
          class="logo-box"
          :class="{ 'logo-box--empty': !displayUrl, 'logo-box--disabled': disabled }"
        >
          <img
            v-if="displayUrl"
            :src="displayUrl"
            alt="Company logo"
            class="logo-image"
          >
          <div
            v-else
            class="text-center grey--text"
          >
            <v-icon
              size="36"
              color="grey lighten-1"
            >
              mdi-image-plus
            </v-icon>
            <div class="caption">
              Click to upload logo
            </div>
          </div>

          <div
            v-if="displayUrl && !disabled"
            class="logo-overlay"
          >
            <v-icon color="white">
              mdi-camera
            </v-icon>
            <span class="caption white--text ml-1">Change</span>
          </div>
        </div>
      </template>
    </helper-image-input>

    <div class="pl-4">
      <span class="font-weight-bold font-calibri font-18 black--text">Company Logo</span>
      <div class="caption grey--text text--darken-1">
        PNG or JPG, up to {{ maxSizeInMb }}MB. 
        A logo with a transparent background works best.
      </div>
      <div
        v-if="value"
        class="caption warning--text"
      >
        New logo will be saved when you click {{ submitLabel }}.
      </div>
      <div
        v-else-if="removed"
        class="caption error--text"
      >
        Logo will be removed when you click {{ submitLabel }}.
        <a @click="$emit('update:removed', false)">Undo</a>
      </div>
      <v-btn
        v-if="displayUrl"
        :disabled="disabled"
        small
        text
        color="error"
        class="mt-1 px-0"
        @click="remove"
      >
        <v-icon
          left
          small
        >
          mdi-delete-outline
        </v-icon>
        Remove
      </v-btn>
    </div>
  </div>
</template>

<script>
  export default {
    components: {
      HelperImageInput: () => import('@/components/helper/ImageInput'),
    },
    props: {
      // Pending upload emitted by ImageInput: { formData, imageURL, imageFile }
      value: {
        type: Object,
        default: null,
      },
      // URL of the logo already saved on the server
      existingUrl: {
        type: String,
        default: null,
      },
      // Whether the saved logo is marked for removal (use with .sync)
      removed: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      submitLabel: {
        type: String,
        default: 'Save',
      },
    },
    data () {
      return {
        // Applies to the picked file; it is shrunk before upload, so it can exceed the server's 2MB limit
        maxSizeInMb: 10,
        // Huge designer files are shrunk to this longest side before trimming
        maxDimension: 1200,
        // Every logo is saved on this 3:2 canvas; the server shrinks it to 600x400
        canvasWidth: 1200,
        canvasHeight: 800,
        acceptTypes: ['image/png', 'image/jpeg'],
      }
    },
    computed: {
      displayUrl () {
        if (this.value) return this.value.imageURL
        if (this.removed) return null

        return this.existingUrl
      },
    },
    beforeDestroy () {
      this.revokePreview()
    },
    methods: {
      onFileSelected (image) {
        if (!this.acceptTypes.includes(image.imageFile.type)) {
          URL.revokeObjectURL(image.imageURL)
          this.$snack({ color: 'error', message: 'Company logo must be a PNG or JPG image' })
          return
        }

        this.resizeImage(image).then(resized => {
          this.revokePreview()
          this.$emit('input', resized)
          this.$emit('update:removed', false)
        })
      },
      // Fit every logo onto the same fixed-size canvas so they all show at the same size on payslips:
      // shrink (designer files are often thousands of pixels, too big for the server to decode),
      // trim the empty margin around the artwork, then centre it with transparent padding
      // (white for JPG, which has no transparency).
      // Promise chain instead of async/await: the build has no regeneratorRuntime.
      resizeImage (image) {
        const file = image.imageFile
        const isPng = file.type === 'image/png'

        return createImageBitmap(file)
          .then(bitmap => {
            // Work on a shrunk copy so trimming scans a manageable number of pixels
            const scale = Math.min(1, this.maxDimension / Math.max(bitmap.width, bitmap.height))
            const work = document.createElement('canvas')
            work.width = Math.round(bitmap.width * scale)
            work.height = Math.round(bitmap.height * scale)

            const workCtx = work.getContext('2d')
            workCtx.imageSmoothingQuality = 'high'
            workCtx.drawImage(bitmap, 0, 0, work.width, work.height)
            bitmap.close()

            const crop = this.contentBounds(workCtx, work.width, work.height)

            // Scale up as well as down so small logos don't end up tiny inside the canvas
            const canvas = document.createElement('canvas')
            canvas.width = this.canvasWidth
            canvas.height = this.canvasHeight
            const fit = Math.min(canvas.width / crop.width, canvas.height / crop.height)
            const drawWidth = Math.round(crop.width * fit)
            const drawHeight = Math.round(crop.height * fit)

            const ctx = canvas.getContext('2d')
            if (!isPng) {
              ctx.fillStyle = '#ffffff'
              ctx.fillRect(0, 0, canvas.width, canvas.height)
            }
            ctx.imageSmoothingQuality = 'high'
            ctx.drawImage(
              work,
              crop.x, crop.y, crop.width, crop.height,
              Math.round((canvas.width - drawWidth) / 2), Math.round((canvas.height - drawHeight) / 2), drawWidth, drawHeight,
            )

            return new Promise(resolve => canvas.toBlob(resolve, file.type, 0.92))
              .then(blob => {
                if (!blob) return image

                const imageFile = new File([blob], file.name, { type: file.type })
                const formData = new FormData()
                formData.append('logo', imageFile)

                URL.revokeObjectURL(image.imageURL)

                return { formData, imageURL: URL.createObjectURL(imageFile), imageFile }
              })
          })
          // Fall back to the original file; the server can still process it
          .catch(() => image)
      },
      // Smallest box holding the artwork, ignoring transparent and near-white margins
      contentBounds (ctx, width, height) {
        const pixels = ctx.getImageData(0, 0, width, height).data
        let top = height
        let left = width
        let bottom = -1
        let right = -1

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4
            const isEmpty = pixels[i + 3] < 10 || (pixels[i] > 245 && pixels[i + 1] > 245 && pixels[i + 2] > 245)

            if (!isEmpty) {
              if (y < top) top = y
              if (y > bottom) bottom = y
              if (x < left) left = x
              if (x > right) right = x
            }
          }
        }

        // Blank image: keep it whole
        if (bottom < 0) return { x: 0, y: 0, width, height }

        return { x: left, y: top, width: right - left + 1, height: bottom - top + 1 }
      },
      remove () {
        // Discard a pending upload first; otherwise mark the saved logo for removal
        if (this.value) {
          this.revokePreview()
          this.$emit('input', null)
        } else {
          this.$emit('update:removed', true)
        }
      },
      revokePreview () {
        if (this.value) URL.revokeObjectURL(this.value.imageURL)
      },
    },
  }
</script>

<style scoped>
.logo-box {
  position: relative;
  width: 200px;
  height: 100px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
}
.logo-box--empty {
  border: 2px dashed #bdbdbd;
}
.logo-box--empty:hover {
  border-color: #757575;
}
.logo-box--disabled {
  pointer-events: none;
  opacity: 0.6;
}
.logo-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  padding: 6px;
}
.logo-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity 0.2s;
}
.logo-box:hover .logo-overlay {
  opacity: 1;
}
.font-18{
  font-size: 18px;
}
.font-calibri{
  font-family: Calibri;
}
</style>
